/**
 * useFmTableCore — sort + expansion state, memoised row pipeline, and the
 * sort/toggle callbacks for FmTable. Extracted so the component body stays a
 * thin orchestrator; behaviour matches the previous inline hooks exactly.
 */
import React from 'react';
import type { FmTableColumn, FmTableProps } from '../../types/fm-table';
import {
  buildInternalRows,
  normalizeKeys,
  sortRows,
  type FmTableInternalRow,
  type SortState,
} from './FmTable.utils';

export interface FmTableCore<T extends object> {
  effectiveSortState: SortState;
  internalRows: FmTableInternalRow<T>[];
  effectiveExpandedKeys: Set<string>;
  handleSort: (col: FmTableColumn<T>) => void;
  toggleRowExpanded: (row: T) => void;
}

export function useFmTableCore<T extends object>(
  props: FmTableProps<T>,
  canSort: boolean,
): FmTableCore<T> {
  const {
    columns,
    rows,
    rowKey,
    groupBy,
    expandable,
    defaultSort,
    sortState: controlledSortState,
    onSortChange,
  } = props;
  const ungroupedLabel = props.ungroupedLabel ?? '—';
  const manualSort = props.manualSort ?? false;

  const [sortState, setSortState] = React.useState<SortState>(() =>
    defaultSort ? { key: defaultSort.key, direction: defaultSort.direction ?? 'ascending' } : null,
  );
  const effectiveSortState = controlledSortState !== undefined ? controlledSortState : sortState;
  const [uncontrolledExpandedKeys, setUncontrolledExpandedKeys] = React.useState<Set<string>>(() =>
    normalizeKeys(expandable?.defaultExpandedRowKeys),
  );

  const sortedRows = React.useMemo(
    () => (canSort && !manualSort ? sortRows(rows, columns, effectiveSortState) : rows),
    [canSort, columns, effectiveSortState, manualSort, rows],
  );
  const internalRows = React.useMemo(
    () => buildInternalRows(sortedRows, rowKey, groupBy, ungroupedLabel),
    [groupBy, rowKey, sortedRows, ungroupedLabel],
  );
  const controlledExpandedKeys = expandable?.expandedRowKeys;
  const expandedKeys = controlledExpandedKeys
    ? normalizeKeys(controlledExpandedKeys)
    : uncontrolledExpandedKeys;
  const expandableRowKeys = React.useMemo(() => {
    if (!expandable) return new Set<string>();
    return new Set(
      rows.filter((row) => expandable.rowExpandable?.(row) ?? true).map((row) => rowKey(row)),
    );
  }, [expandable, rowKey, rows]);
  const effectiveExpandedKeys = React.useMemo(() => {
    if (!expandable) return expandedKeys;
    return new Set(Array.from(expandedKeys).filter((key) => expandableRowKeys.has(key)));
  }, [expandable, expandableRowKeys, expandedKeys]);

  const setExpandedKeys = (nextKeys: Set<string>): void => {
    const keys = Array.from(nextKeys);
    if (!controlledExpandedKeys) {
      setUncontrolledExpandedKeys(nextKeys);
    }
    expandable?.onExpandedRowsChange?.(keys);
  };

  const toggleRowExpanded = (row: T): void => {
    if (!expandable) return;
    const key = rowKey(row);
    if (expandable.rowExpandable && !expandable.rowExpandable(row)) return;

    const expandMode = expandable.expandMode ?? 'multi';
    const nextKeys = new Set<string>();

    if (expandMode === 'single') {
      // Single-expand mode: only one row can be expanded at a time (accordion)
      if (effectiveExpandedKeys.has(key)) {
        // Row is already expanded, collapse it
        // nextKeys is empty (no rows expanded)
      } else {
        // Row is not expanded, expand it (clearing any previously expanded row)
        nextKeys.add(key);
      }
    } else {
      // Multi-expand mode (default): multiple rows can be expanded simultaneously
      // Toggle the current row's expanded state
      if (effectiveExpandedKeys.has(key)) {
        // Row is expanded, remove it from the set
        effectiveExpandedKeys.forEach((k) => {
          if (k !== key) {
            nextKeys.add(k);
          }
        });
      } else {
        // Row is not expanded, add it to the set (keeping other expanded rows)
        effectiveExpandedKeys.forEach((k) => nextKeys.add(k));
        nextKeys.add(key);
      }
    }

    setExpandedKeys(nextKeys);
  };

  const handleSort = (col: FmTableColumn<T>): void => {
    if (!col.sortable || !canSort) return;
    const nextState = (() => {
      if (effectiveSortState?.key !== col.key) {
        return { key: col.key, direction: 'ascending' as const };
      }
      if (effectiveSortState.direction === 'ascending') {
        return { key: col.key, direction: 'descending' as const };
      }
      return null;
    })();

    if (controlledSortState === undefined) {
      setSortState(nextState);
    }
    onSortChange?.(nextState);
  };

  return {
    effectiveSortState,
    internalRows,
    effectiveExpandedKeys,
    handleSort,
    toggleRowExpanded,
  };
}

/** Classes FmTable.css keys the fitted layouts on. */
export const FM_TABLE_LABELLED_CLASS = 'fm-table--labelled-cells';
export const FM_TABLE_PINNED_CLASS = 'fm-table--pinned-first';
export const FM_TABLE_NARROW_CLASS = 'fm-table--narrow';

/** Below this container width (in root ems) a row reads better as a labelled card. */
const CARD_CONTAINER_MAX_REM = 30;
/** A pinned first column wider than this share of the container would cover the rest. */
const PINNED_FIRST_MAX_SHARE = 0.4;

interface TableFit {
  cards: boolean;
  /** Cards in a phone-width container, where mobile-hidden columns drop out. */
  narrow: boolean;
  pinnedFirst: boolean;
}

function measureFit(host: HTMLElement, labelled: boolean): TableFit {
  const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  if (labelled && host.clientWidth < CARD_CONTAINER_MAX_REM * rootFontSize) {
    return { cards: true, narrow: true, pinnedFirst: false };
  }
  // Measure the grid's natural width with the card class lifted, synchronously
  // inside the layout effect, so the measurement never paints.
  const hadCards = host.classList.contains(FM_TABLE_LABELLED_CLASS);
  if (hadCards) host.classList.remove(FM_TABLE_LABELLED_CLASS);
  const overflows = host.scrollWidth > host.clientWidth + 1;
  const firstCell = host.querySelector<HTMLElement>('tr > :first-child');
  if (hadCards) host.classList.add(FM_TABLE_LABELLED_CLASS);
  if (!overflows) return { cards: false, narrow: false, pinnedFirst: false };
  if (labelled) return { cards: true, narrow: false, pinnedFirst: false };
  const firstWidth = firstCell?.offsetWidth ?? host.clientWidth;
  return {
    cards: false,
    narrow: false,
    pinnedFirst: firstWidth <= host.clientWidth * PINNED_FIRST_MAX_SHARE,
  };
}

/**
 * True while the host container is narrower than the card threshold, so an
 * element whose phone reading differs from its grid (the comparison matrix)
 * switches on the same width the shared table does.
 */
export function useNarrowContainer(): {
  ref: React.RefObject<HTMLDivElement | null>;
  narrow: boolean;
} {
  const ref = React.useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = React.useState(false);
  React.useLayoutEffect(() => {
    const host = ref.current;
    if (!host || typeof ResizeObserver === 'undefined') return undefined;
    const evaluate = (): void => {
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
      setNarrow(host.clientWidth < CARD_CONTAINER_MAX_REM * rootFontSize);
    };
    evaluate();
    const observer = new ResizeObserver(evaluate);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);
  return { ref, narrow };
}

/**
 * Fits the grid to its container. A labelled table becomes card rows when the
 * container is narrower than ~30rem or the grid would overflow it: a money
 * column parked behind horizontal scroll is a value the owner never reads. A
 * table that must scroll pins only its first column, which names the row, and
 * only while that column is narrow enough to leave the values readable.
 */
export function useTableFit(
  labelled: boolean,
  contentKey: string,
): { ref: React.RefObject<HTMLDivElement | null> } & TableFit {
  const ref = React.useRef<HTMLDivElement>(null);
  const [fit, setFit] = React.useState<TableFit>({
    cards: false,
    narrow: false,
    pinnedFirst: false,
  });
  React.useLayoutEffect(() => {
    const host = ref.current;
    if (!host || typeof ResizeObserver === 'undefined') return undefined;
    const evaluate = (): void => {
      const next = measureFit(host, labelled);
      setFit((prev) =>
        prev.cards === next.cards &&
        prev.narrow === next.narrow &&
        prev.pinnedFirst === next.pinnedFirst
          ? prev
          : next,
      );
    };
    evaluate();
    const observer = new ResizeObserver(evaluate);
    observer.observe(host);
    return () => observer.disconnect();
  }, [labelled, contentKey]);
  return { ref, ...fit };
}
